<template>
    <div>
        <div class="c-tr">
            <div class="c-td is-1">
                <span v-text="'Status'" class="typo-label" />
                <a-icon
                    v-tooltip="status.tooltip"
                    :icon="status.icon"
                    :class="status.class"
                    height="16"
                />
            </div>

            <div class="c-td is-2">
                <span v-text="'Numer kuponu'" class="typo-label" />
                <strong v-text="couponNumber" />
                <span v-text="'Źródło przyjęcia'" class="typo-label text-xs font-weight-light" />
                <p v-text="coupon.coupon_source || '—'" class="text-xs mb-0" />
            </div>

            <div class="c-td is-3">
                <span v-text="'Data wystawienia'" class="typo-label" />
                <div v-text="issueDate.date" />
                <p v-text="issueDate.time" class="text-xs mb-0" />
            </div>

            <div class="c-td is-4">
                <span v-text="'Data wykorzystania'" class="typo-label" />
                <div v-text="utilisedDate.date" />
                <p v-text="utilisedDate.time" class="text-xs mb-0" />
            </div>

            <div class="c-td is-5">
                <span v-text="'Liczba opakowań'" class="typo-label" />
                <div class="d-inline-flex align-items-center">
                    <span v-text="packagesCount" />
                    <span class="d-flex cursor-pointer ml-1" @click="openEans">
                        <a-icon icon="Eye" width="16" />
                    </span>
                </div>
            </div>

            <div class="c-td is-6">
                <span v-text="'Wartość'" class="typo-label" />
                {{ couponValue }}
            </div>

            <div class="c-td is-7">
                <span v-text="'Wykorzystany'" class="typo-label" />
                {{ isUtilised ? 'Tak' : 'Nie' }}
            </div>

            <div class="c-td is-8">
                <the-btn
                    class="btn-gray btn-sm px-1 d-none d-xl-inline-flex"
                    :icon="expanded ? 'ArrowUp' : 'ArrowDown'"
                    @click="expanded = !expanded"
                />

                <the-btn
                    class="btn-gray btn-sm d-xl-none"
                    text="Szczegóły"
                    :icon="expanded ? 'ArrowUp' : 'ArrowDown'"
                    @click="expanded = !expanded"
                />
            </div>
        </div>

        <div v-if="expanded" class="coupon-details">
            <div class="coupon-details__row">
                <strong v-text="'Operator:'" />
                <span v-text="coupon.operator_name || '—'" />
            </div>
            <div class="coupon-details__row">
                <strong v-text="'Sposób wykorzystania:'" />
                <span v-text="coupon.coupon_utilise_type || '—'" />
            </div>
            <div class="coupon-details__row">
                <strong v-text="'ID urządzenia wystawiającego:'" />
                <span v-text="coupon.mcuid || '—'" />
            </div>
            <div class="coupon-details__row">
                <strong v-text="'ID urządzenia wykorzystującego:'" />
                <span v-text="coupon.confirm_mcuid || '—'" />
            </div>
            <div
                v-for="{ label, status, id } in reportStatuses"
                :key="id"
                class="coupon-details__row"
            >
                <strong>{{ label }}:</strong>

                <span class="d-flex align-items-center gap-0-5 mb-2">
                    <a-icon
                        :icon="status.icon"
                        :class="status.class"
                        height="16"
                        width="16"
                    />
                    {{ status.message }}
                </span>

                <template v-if="status.error">
                    <span class="error-message">
                        Skopiuj treść błędu i przekaż go na HelpDesk
                        <a-icon
                            icon="Copy"
                            @click.native="copyToClipboard(status.error, id)"
                        />
                        <transition name="fade">
                            <span
                                v-if="copied === id"
                                v-text="'Skopiowano'"
                                class="text-success"
                            />
                        </transition>
                    </span>

                    <the-text-area
                        readonly
                        :value="status.error"
                    />
                </template>
            </div>
        </div>
    </div>
</template>

<script>
const COUPON_STATUS_AVAILABLE_FROM = 1761692400000

export default {
    props: {
        coupon: {
            type: Object,
            required: true
        }
    },

    data: () => ({
        expanded: false,
        copied: null
    }),

    computed: {
        couponNumber() {
            const number = this.coupon.coupon_number
            if (!number) return '-'
            if (this.isUtilised) return number

            return `${number.slice(0, 2)}****${number.slice(number.length - 4, number.length)}`
        },

        issueDate() {
            return this.formatDateTime(this.coupon.issue_date)
        },

        utilisedDate() {
            return this.formatDateTime(this.coupon.utilised_date)
        },

        isUtilised() {
            return !!this.coupon.utilised_date
        },

        couponValue() {
            return this.coupon.coupon_value == null
                ? '—'
                : this.$options.filters.currency(this.coupon.coupon_value / 100)
        },

        packagesCount() {
            return (this.coupon.coupon_eans || []).reduce(
                (total, item) => total + Number(item.factor || 0),
                0
            )
        },

        isOldCoupon() {
            return this.coupon.issue_date && this.coupon.issue_date < COUPON_STATUS_AVAILABLE_FROM
        },

        isTransactionStatusOk() {
            return this.coupon.report_transaction === null ||
                this.coupon.report_transaction.processing_result === 200
        },

        isRedeemStatusOk() {
            return this.coupon.report_redeem === null ||
                this.coupon.report_redeem.processing_result === 200
        },

        isStatusOk() {
            return this.isTransactionStatusOk && this.isRedeemStatusOk
        },

        status() {
            if (this.isOldCoupon) {
                return {
                    icon: 'Ban',
                    class: 'text-muted',
                    tooltip: 'Brak informacji o statusie'
                }
            }

            if (!this.isStatusOk) {
                return {
                    icon: 'WarningFull',
                    class: 'text-orange',
                    tooltip: 'Wystąpił problem z kuponem. Rozwiń szczegóły, aby zobaczyć treść błędu.'
                }
            }

            return {
                icon: 'Ok',
                tooltip: 'Status prawidłowy'
            }
        },

        reportStatuses() {
            return [
                {
                    id: 'transaction',
                    label: 'Status wystawienia',
                    status: this.getReportStatus(this.coupon.report_transaction)
                },
                {
                    id: 'redeem',
                    label: 'Status wykorzystania',
                    status: this.getReportStatus(this.coupon.report_redeem)
                }
            ]
        }
    },

    methods: {
        formatDateTime(date) {
            if (!date) {
                return {
                    date: '—',
                    time: null
                }
            }

            const moment = this.$moment(date)

            return {
                date: moment.format('DD-MM-YYYY'),
                time: moment.format('HH:mm')
            }
        },

        getReportStatus(report) {
            if (!report) {
                return {
                    icon: 'Ban',
                    class: 'text-muted',
                    message: 'Brak informacji o statusie'
                }
            }

            if (report.processing_result === 200) {
                return {
                    icon: 'Ok',
                    message: 'Brak błędów'
                }
            }

            return {
                icon: 'WarningFull',
                class: 'text-orange',
                message: 'Wystąpił problem z kuponem',
                error: report.result_message
            }
        },

        copyToClipboard(error, id) {
            window.navigator.clipboard.writeText(error)

            this.copied = id
            clearTimeout(this.copyTimeout)
            this.copyTimeout = setTimeout(() => {
                this.copied = null
            }, 2000)
        },

        openEans() {
            this.$store.dispatch('openModal', {
                componentPath: 'drs/reports/UsedCouponEans',
                args: { couponEans: this.coupon.coupon_eans || [] }
            })
        }
    }
}
</script>

<style lang="scss" scoped>
.coupon-details {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 1rem;
    margin: 0 1rem;
    padding: 1rem 0;
    border-top: 1px solid var(--color-gray-200);

    &__row {
        display: flex;
        flex-direction: column;

        .error-message .a-icon {
            color: var(--color-orange);
            cursor: pointer;
            margin-right: 0.25rem;
            vertical-align: sub;
            height: 1rem;
            width: 1rem;
        }
    }
}

@media (max-width: 767px) {
    .coupon-details {
        grid-template-columns: 1fr;
    }
}
</style>
